
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, Users, ClipboardList, AlertCircle, CheckCircle, List, FileUp, Snowflake, Play } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import UploadGradeReport from "../components/admin/UploadGradeReport";
import UploadASDRoster from "../components/admin/UploadASDRoster";
import RecentReports from "../components/admin/RecentReports";
import { format } from "date-fns";

export default function AdminDashboard() {
  const [showUpload, setShowUpload] = useState(false);
  const [showASDUpload, setShowASDUpload] = useState(false);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  // Check if user has admin access
  const isMainAdmin = user?.role === 'admin';
  const isAP = user?.role === 'user' && user?.user_type === 'assistant_principal';
  const isPrincipal = user?.role === 'user' && user?.user_type === 'principal';
  const hasAdminAccess = isMainAdmin || isAP || isPrincipal;

  const { data: rosterEntries = [] } = useQuery({
    queryKey: ['rosterEntries'],
    queryFn: () => base44.entities.RosterEntry.list('-created_date'),
  });

  const { data: students = [] } = useQuery({
    queryKey: ['students'],
    queryFn: () => base44.entities.Student.list(),
  });

  const { data: reports = [] } = useQuery({
    queryKey: ['gradeReports'],
    queryFn: () => base44.entities.GradeReport.list('-created_date', 10),
  });

  const { data: excusals = [] } = useQuery({
    queryKey: ['excusals'],
    queryFn: () => base44.entities.ExcusalSubmission.list('-created_date'),
  });

  // Guard - only admins and APs can access (no UI shown, redirect handled by Layout)
  if (!hasAdminAccess) {
    return null; // Layout will handle the redirect
  }

  // Count unique students with priority: P > R > A > E > Pending
  // Rule: P and R take precedence over E. A student is counted only once.
  const getStudentCounts = () => {
    const studentMap = new Map(); // other_id -> { status: highest_priority_status, name: student_name, entryCount: total_entries_for_this_student }
    
    // Process all EA roster entries (active + excused)
    const eaEntries = rosterEntries.filter(e => e.status === 'active' || e.status === 'excused');
    
    eaEntries.forEach(entry => {
      const otherId = entry.other_id;
      const currentData = studentMap.get(otherId);
      
      // Determine this entry's status
      let entryStatus;
      if (entry.status === 'excused') {
        entryStatus = 'excused';
      } else {
        entryStatus = entry.attendance_status || 'pending';
      }
      
      // Priority: present > refusal > absent > excused > pending
      // P and R take precedence over E
      const priority = {
        'present': 5,
        'refusal': 4,
        'absent': 3,
        'excused': 2,
        'pending': 1
      };

      const newEntryCount = (currentData?.entryCount || 0) + 1;
      const newName = `${entry.student_first_name || ''} ${entry.student_last_name || ''}`.trim();
      
      if (!currentData || priority[entryStatus] > priority[currentData.status]) {
        studentMap.set(otherId, {
          status: entryStatus,
          name: newName,
          entryCount: newEntryCount
        });
      } else {
        // If current status is higher or equal priority, keep current highest status, but still increment total entry count
        studentMap.set(otherId, {
          ...currentData,
          entryCount: newEntryCount // Only update total count
        });
      }
    });
    
    // Count by final status
    let presentCount = 0;
    let excusedCount = 0;
    let absentCount = 0;
    let refusalCount = 0;
    let pendingCount = 0;
    
    studentMap.forEach(data => {
      if (data.status === 'present') presentCount++;
      else if (data.status === 'excused') excusedCount++;
      else if (data.status === 'absent') absentCount++;
      else if (data.status === 'refusal') refusalCount++;
      else pendingCount++;
    });
    
    return {
      activeCount: studentMap.size,
      presentCount,
      excusedCount,
      absentCount,
      refusalCount,
      pendingCount,
      studentMap // Return the map for debug view
    };
  };

  const counts = getStudentCounts();
  
  // Count ASD students (unique)
  const refusalStudents = new Set(rosterEntries.filter(e => e.status === 'asd').map(e => e.other_id));
  const refusalCount = refusalStudents.size;

  const pendingExcusals = excusals.filter(e => e.status === 'pending').length;

  // NEW: Get pending manual assignments count
  const { data: pendingManualAssignments = [] } = useQuery({
    queryKey: ['pendingManualAssignments'],
    queryFn: async () => {
      const allAssignments = await base44.entities.ManualAssignment.list();
      return allAssignments.filter(a => a.status === 'pending');
    },
  });

  const pendingManualCount = pendingManualAssignments.length;

  // System freeze toggle: writes / updates a Settings row so every logged-in
  // student and teacher gets a full-screen "Closeout in process" overlay via
  // Layout.jsx that blocks every form submission and interaction.
  const { data: settings = [] } = useQuery({
    queryKey: ['settings'],
    queryFn: () => base44.entities.Settings.list(),
  });
  const freezeSetting = settings.find((s) => s.setting_key === 'system_freeze');
  const isSystemFrozen = freezeSetting?.frozen === true;

  const toggleFreezeMutation = useMutation({
    mutationFn: async () => {
      const nextFrozen = !isSystemFrozen;
      if (freezeSetting?.id) {
        await base44.entities.Settings.update(freezeSetting.id, { frozen: nextFrozen });
      } else {
        await base44.entities.Settings.create({ setting_key: 'system_freeze', frozen: nextFrozen });
      }
      return nextFrozen;
    },
    onSuccess: (frozen) => {
      queryClient.invalidateQueries(['settings']);
      // The overlay reads the same 'settings' query so it will react instantly.
    },
  });

  // Pull teacher assignments mutation
  const pullTeacherAssignmentsMutation = useMutation({
    mutationFn: async () => {
      let pulledCount = 0;
      
      for (const assignment of pendingManualAssignments) {
        // Create RosterEntry from manual assignment
        await base44.entities.RosterEntry.create({
          assignment_type: 'manual',
          campus: 'WPK8',
          student_last_name: assignment.student_last_name,
          student_first_name: assignment.student_first_name,
          other_id: assignment.student_id,
          grade_level: assignment.grade_level,
          course_short_description: assignment.subject,
          teacher: assignment.teacher_name,
          teacher_email: assignment.teacher_email,
          assignment: assignment.assignment,
          date: format(new Date(), 'yyyy-MM-dd'),
          time: format(new Date(), 'HH:mm:ss'),
          f1g1_parent_phone: assignment.f1g1_parent_phone || '',
          f1g2_parent_phone: assignment.f1g2_parent_phone || '',
          student_email: assignment.student_email || '',
          f1g1_parent_email: assignment.f1g1_parent_email || '',
          f2g1_parent_phone: assignment.f2g1_parent_phone || '',
          f2g1_parent_email: assignment.f2g1_parent_email || '',
          status: 'active',
          attendance_status: 'pending'
        });
        
        // Mark manual assignment as pulled
        await base44.entities.ManualAssignment.update(assignment.id, {
          status: 'pulled_to_roster'
        });
        
        pulledCount++;
      }
      
      return pulledCount;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries(['pendingManualAssignments']);
      queryClient.invalidateQueries(['rosterEntries']);
      alert(`✅ Successfully pulled ${count} teacher-assigned students to EA roster!`);
    },
    onError: (err) => {
      alert(`❌ Failed to pull assignments: ${err.message}`);
    }
  });

  // Sort students by Other ID for debug view
  const sortedStudentsList = Array.from(counts.studentMap.entries())
    .sort((a, b) => {
      const idA = a[0] || '';
      const idB = b[0] || '';
      return idA.localeCompare(idB);
    });

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-600 mt-1">Eagle Academy Management Overview</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                const next = !isSystemFrozen;
                const msg = next
                  ? 'Freeze the system? All students and teachers will see the "Closeout in process" banner and be blocked from every form and action until you unfreeze.'
                  : 'Unfreeze the system? Students and teachers will be able to submit forms and take actions again immediately.';
                if (window.confirm(msg)) toggleFreezeMutation.mutate();
              }}
              disabled={toggleFreezeMutation.isPending}
              data-testid="freeze-system-toggle"
              className={
                isSystemFrozen
                  ? 'bg-green-600 hover:bg-green-700 shadow-lg'
                  : 'bg-slate-800 hover:bg-slate-900 shadow-lg'
              }
            >
              {isSystemFrozen ? (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Unfreeze System
                </>
              ) : (
                <>
                  <Snowflake className="w-4 h-4 mr-2" />
                  Freeze System
                </>
              )}
            </Button>
            <Button 
              onClick={() => setShowDebugModal(true)}
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <List className="w-4 h-4 mr-2" />
              View Student List
            </Button>
            <Button 
              onClick={() => setShowASDUpload(true)}
              className="bg-red-600 hover:bg-red-700 shadow-lg"
            >
              <FileUp className="w-4 h-4 mr-2" />
              Upload ASD Roster
            </Button>
            <Button 
              onClick={() => setShowUpload(true)}
              className="bg-red-800 hover:bg-red-900 shadow-lg"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Failure Report
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-none shadow-lg bg-gradient-to-br from-red-800 to-red-900 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Active Assignments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold">{counts.activeCount}</div>
                <ClipboardList className="w-10 h-10 opacity-80" />
              </div>
              <p className="text-xs mt-2 opacity-75">Unique Students</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-red-700 to-red-800 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Total Students</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold">{students.length}</div>
                <Users className="w-10 h-10 opacity-80" />
              </div>
              <p className="text-xs mt-2 opacity-75">In System</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-red-600 to-red-700 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Refusals (ASD)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold">{refusalCount}</div>
                <AlertCircle className="w-10 h-10 opacity-80" />
              </div>
              <p className="text-xs mt-2 opacity-75">Unique Students</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-red-700 to-red-800 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Excused</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold">{counts.excusedCount}</div>
                <CheckCircle className="w-10 h-10 opacity-80" />
              </div>
              <p className="text-xs mt-2 opacity-75">Unique Students</p>
            </CardContent>
          </Card>
        </div>

        {pendingExcusals > 0 && (
          <Card className="border-none shadow-lg bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-orange-700" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-orange-900">Pending Excusal Requests</p>
                    <p className="text-sm text-orange-700">{pendingExcusals} requests awaiting review</p>
                  </div>
                </div>
                <Badge className="bg-orange-600 text-white text-lg px-4 py-2">
                  {pendingExcusals}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {pendingManualCount > 0 && (
          <Card className="border-none shadow-lg bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-green-900">Teacher-Assigned Students Ready</p>
                    <p className="text-sm text-green-700">{pendingManualCount} students waiting to be pulled to EA roster</p>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    if (confirm(`Pull ${pendingManualCount} teacher-assigned students to the EA roster?\n\nThis will add them to the active EA roster with manual assignment badges.`)) {
                      pullTeacherAssignmentsMutation.mutate();
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={pullTeacherAssignmentsMutation.isPending}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Pull {pendingManualCount} to Roster
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <RecentReports reports={reports} />

        {/* Debug Modal */}
        {showDebugModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-4xl max-h-[80vh] flex flex-col">
              <CardHeader className="border-b">
                <CardTitle>Student List ({counts.activeCount} Students)</CardTitle>
                <p className="text-sm text-slate-600">All unique students counted in &quot;Active Assignments&quot;</p>
              </CardHeader>
              <CardContent className="p-6 overflow-y-auto flex-1">
                <Alert className="mb-4 bg-blue-50 border-blue-200">
                  <AlertDescription className="text-blue-900">
                    <strong>How This Works:</strong> Each student is counted once by their <strong>Other ID</strong>. If a student has multiple assignments, only their highest priority status is shown (P &gt; R &gt; A &gt; E &gt; Pending).
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  {sortedStudentsList.map(([otherId, data]) => (
                    <div key={otherId} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <Badge className="font-mono text-sm bg-slate-700 text-white">
                          {otherId || 'NO ID'}
                        </Badge>
                        <span className="font-medium">{data.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          data.status === 'present' ? 'bg-green-500 text-white' :
                          data.status === 'refusal' ? 'bg-red-500 text-white' :
                          data.status === 'absent' ? 'bg-yellow-500 text-white' :
                          data.status === 'excused' ? 'bg-orange-500 text-white' :
                          'bg-slate-300 text-slate-700'
                        }>
                          {data.status === 'present' ? 'P' :
                           data.status === 'refusal' ? 'R' :
                           data.status === 'absent' ? 'A' :
                           data.status === 'excused' ? 'E' :
                           'Pending'}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {data.entryCount} {data.entryCount === 1 ? 'assignment' : 'assignments'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
              <div className="border-t p-4">
                <Button 
                  onClick={() => setShowDebugModal(false)}
                  className="w-full"
                >
                  Close
                </Button>
              </div>
            </Card>
          </div>
        )}

        {showUpload && (
          <UploadGradeReport 
            onClose={() => setShowUpload(false)}
            onSuccess={() => {
              setShowUpload(false);
              queryClient.invalidateQueries(['rosterEntries']);
              queryClient.invalidateQueries(['gradeReports']);
            }}
          />
        )}

        {showASDUpload && (
          <UploadASDRoster 
            onClose={() => setShowASDUpload(false)}
            onSuccess={() => {
              setShowASDUpload(false);
              queryClient.invalidateQueries(['rosterEntries']);
            }}
          />
        )}
      </div>
    </div>
  );
}
